import {
  ProductionPlanFormData,
  ProductionPlanItem,
} from "../../views/Distribution/Order Management & Allocation/Production Entry/Add Production Plan";
import { apiSlice } from "../apiSlice";
import {
  CartTypesResponse,
  UpdateBulkCartLoadingConfigurationRequest,
  UpdateCartLoadingConfigurationRequest,
} from "../cartConfigurationSlice";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { CartLoadingDetailResponse, CreateGroupOrderRequest, GetCartLoadingDetailParams, UpdateCartLoadingStateRequest } from "../cartLoadingSlice";
import {
  DistributeOrdersRequest,
  DistributionBasicPlanResponse,
  GetDistributionBasicPlanParams,
} from "../distributionSlice";

export interface ProductionPlanResponse {
  id: string;
  displayName: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  items: ProductionPlanItem[];
}

export interface getAllProductionPlans {
  planId: string;
  displayName: string;
  isActive: boolean;
}

export interface GetProductionPlanParams {
  planId: string;
  restaurantId: string;
}

export interface UpdateProductionPlanRequest {
  planId: string;
  restaurantId: string;
  displayName: string;
  items: ProductionPlanItem[];
}

export interface DeleteProductionPlanParams {
  planId: string;
  restaurantId: string;
}

export interface UpdateActiveProductionPlanRequest {
  restaurantId: string;
  planId: string;
  isActive: boolean;
}

export interface AllocationItem {
  productItemId: string;
  abbreviation?: string;
  displayName: string;
  portionSize: string;
  directIssues: number;
  // pickupStock?: number;
}

export interface CartAllocationsResponse {
  [cartId: string]: {
    cartName: string;
    allocations: AllocationItem[];
  };
}

export interface GetCartAllocationsParams {
  restaurantId: string;
  inventoryId: string;
}

export interface CartAllocationRequest {
  cartId: string;
  cartName: string;
  abbreviation?: string;
  allocations: AllocationItem[];
}

export interface CreateCartAllocationRequest {
  restaurantId: string;
  allocationPlanVersion: string;
  carts: CartAllocationRequest[];
}

export interface CartAllocationPlan {
  planId: string;
  allocationPlanVersion: string;
  isActive: boolean;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
}

export interface CartAllocationPlanDetail {
  id: string;
  carts: CartAllocationRequest[];
  allocationPlanVersion: string;
  isActive: boolean;
  status: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
}

export interface GetCartAllocationPlanParams {
  restaurantId: string;
  planId: string;
}

export interface UpdateActiveCartAllocationPlanRequest {
  restaurantId: string;
  planId: string;
  isActive: boolean;
}

export interface UpdateCartAllocationPlanRequest {
  restaurantId: string;
  planId: string;
  allocationPlanVersion: string;
  carts: CartAllocationRequest[];
}

export interface DeleteCartAllocationPlanParams {
  planId: string;
  restaurantId: string;
}

export interface SaveCartDistributionPlanRequest {
  restaurantId: string;
  availableItems: Array<{
    productItemId: string;
    availableCount: number;
  }>;
  carts: Array<{
    cartId: string;
    cartName: string;
    allocations: Array<{
      productItemId: string;
      productName: string;
      portionSize: "SMALL" | "REGULAR" | "LARGE";
      directIssues: number;
      reservedIssues: number;
      totalIssues: number;
    }>;
  }>;
}

export interface SaveCartDistributionPlanResponse {
  success: boolean;
  message: string;
  planId?: string;
}

const API_BASE_DOMAIN = process.env.REACT_APP_API_BASE_DOMAIN_PROD;

export const distributionService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Production Plan
    createProductionPlan: builder.mutation<string, ProductionPlanFormData>({
      query: (data) => ({
        url: `https://adminhttps-createproductionplanforrestaurant${API_BASE_DOMAIN}`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProductionPlans', id: arg.restaurantId }
      ],
    }),

    updateProductionPlan: builder.mutation<string, UpdateProductionPlanRequest>(
      {
        query: (data) => ({
          url: `https://adminhttps-updateproductionplan${API_BASE_DOMAIN}`,
          method: "PUT",
          body: data,
          responseHandler: (response) => response.text(),
        }),
         invalidatesTags: (result, error, arg) => [
        { type: 'ProductionPlan', id: arg.planId },
        { type: 'ProductionPlans', id: arg.restaurantId }
      ],
      }
    ),

    updateActiveProductionPlan: builder.mutation<
      string,
      UpdateActiveProductionPlanRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-updateactiveproductionplan${API_BASE_DOMAIN}`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
      
    }),

    deleteProductionPlan: builder.mutation<string, DeleteProductionPlanParams>({
      query: ({ planId, restaurantId }) => ({
        url: `https://adminhttps-deleteproductionplan${API_BASE_DOMAIN}?restaurantId=${restaurantId}&planId=${planId}`,
        method: "DELETE",
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProductionPlan', id: arg.planId },
        { type: 'ProductionPlans', id: arg.restaurantId }
      ],
    }),

    getAllProductionPlans: builder.query<getAllProductionPlans[], string>({
      query: (restaurantId) => ({
        url: `https://adminhttps-getproductionplanslist${API_BASE_DOMAIN}/?restaurantId=${restaurantId}`,
        method: "GET",
      }),
      // Transform the response to match our interface
      transformResponse: (response: any[]) => {
        return response.map((plan) => ({
          planId: plan.planId,
          displayName: plan.displayName,
          isActive: plan.isActive,
        }));
      },
      providesTags: (result, error, restaurantId) => [
        { type: 'ProductionPlans', id: restaurantId }
      ],
    }),

    getProductionPlan: builder.query<
      ProductionPlanResponse,
      GetProductionPlanParams
    >({
      query: ({ planId, restaurantId }) => ({
        url: `https://adminhttps-getrestaurantproductionplan${API_BASE_DOMAIN}/?planId=${planId}&restaurantId=${restaurantId}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => [
        { type: 'ProductionPlan', id: arg.planId }
      ],
    }),

    // Cart Allocation
    getCartAllocations: builder.query<
      CartAllocationsResponse,
      GetCartAllocationsParams
    >({
      query: ({ restaurantId, inventoryId }) => ({
        url: `https://adminhttps-getcartallocationbasicplan${API_BASE_DOMAIN}/?restaurantId=${restaurantId}&inventoryId=${inventoryId}`,
        method: "GET",
      }),
    }),

    getAllCartAllocationPlans: builder.query<CartAllocationPlan[], string>({
      query: (restaurantId) => ({
        url: `https://adminhttps-getcartallocationslist${API_BASE_DOMAIN}/?restaurantId=${restaurantId}`,
        method: "GET",
      }),
    }),

    getCartAllocationPlanById: builder.query<
      CartAllocationPlanDetail,
      GetCartAllocationPlanParams
    >({
      query: ({ restaurantId, planId }) => ({
        url: `https://adminhttps-getcartallocationplanbyid${API_BASE_DOMAIN}/?restaurantId=${restaurantId}&planId=${planId}`,
        method: "GET",
      }),
    }),

    updateActiveCartAllocationPlan: builder.mutation<
      string,
      UpdateActiveCartAllocationPlanRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-updateactivecartallocationplan${API_BASE_DOMAIN}`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),

    updateCartAllocationPlan: builder.mutation<
      string,
      UpdateCartAllocationPlanRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-updatecartallocationplan${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),

    createCartAllocation: builder.mutation<string, CreateCartAllocationRequest>(
      {
        query: (data) => ({
          url: `https://adminhttps-createcartallocationforrestaurant${API_BASE_DOMAIN}`,
          method: "POST",
          body: data,
          responseHandler: (response) => response.text(),
        }),
      }
    ),

    deleteCartAllocationPlan: builder.mutation<
      string,
      DeleteCartAllocationPlanParams
    >({
      query: ({ planId, restaurantId }) => ({
        url: `https://adminhttps-deletecartallocationplan${API_BASE_DOMAIN}/?restaurantId=${restaurantId}&planId=${planId}`,
        method: "DELETE",
        responseHandler: (response) => response.text(),
      }),
    }),

    // Distribution Plan
    getDistributionBasicPlan: builder.query<
      DistributionBasicPlanResponse,
      GetDistributionBasicPlanParams
    >({
      query: ({ restaurantId }) => ({
        url: `https://adminhttps-getdistributionbasicplan${API_BASE_DOMAIN}/?restaurantId=${restaurantId}`,
        method: "GET",
      }),
    }),

    saveCartDistributionPlan: builder.mutation<
      string,
      SaveCartDistributionPlanRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-savecartdistributionplan${API_BASE_DOMAIN}`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),

    distributeOrders: builder.mutation<any, DistributeOrdersRequest>({
      query: (data) => ({
        url: `https://adminhttps-distributeorder${API_BASE_DOMAIN}`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),

    // Cart Configuration
    updateCartLoadingConfiguration: builder.mutation<
      string,
      UpdateCartLoadingConfigurationRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-updatecartloadingconfiguration${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),

    getCartTypes: builder.query<CartTypesResponse, void>({
      query: () => ({
        url: `https://adminhttps-getcarttypes${API_BASE_DOMAIN}`,
        method: "GET",
      }),
    }),

    updateBulkCartLoadingConfiguration: builder.mutation<
      string,
      UpdateBulkCartLoadingConfigurationRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-updatebulkcartloadingconfiguration${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),
    updateCartLoadingState: builder.mutation<
      string,
      UpdateCartLoadingStateRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-updatecartloadingstate${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),
    getCartsLoadForRestaurantByRestaurantId: builder.query({
      query: (restaurantId) =>
        `https://adminhttps-getrestaurantcartloadings${API_BASE_DOMAIN}/?restId=${restaurantId}`,
    }),
     getCartLoadingDetail: builder.query<CartLoadingDetailResponse, any>({
  query: ({ restId, cartId }) => ({
    url: `https://adminhttps-getcartloadingsforcart${API_BASE_DOMAIN}/?restId=${restId}&cartId=${cartId}`,
    method: "GET",
  }),
    providesTags: (result, error, arg) => [
    { type: 'CartLoadingDetail', id: arg?.cartId }
  ],
}),
  createGroupOrder: builder.mutation<any, CreateGroupOrderRequest>({
  query: (data) => ({
    url: `https://adminhttps-creategrouporder${API_BASE_DOMAIN}`,
    method: "POST",
    body: data,
  }),
 invalidatesTags: (result, error, arg) => [
    { type: 'CartLoading', id: 'LIST' },
    { type: 'CartLoadingDetail', id: arg.groupOrders[0]?.cartId }
  ],
}),

  }),
});

export const {
  useCreateProductionPlanMutation,
  useUpdateProductionPlanMutation,
  useDeleteProductionPlanMutation,
  useLazyGetAllProductionPlansQuery,
  useLazyGetProductionPlanQuery,
  useUpdateActiveProductionPlanMutation,
  useLazyGetCartAllocationsQuery,
  useCreateCartAllocationMutation,
  useGetAllCartAllocationPlansQuery,
  useLazyGetAllCartAllocationPlansQuery,
  useLazyGetCartAllocationPlanByIdQuery,
  useUpdateActiveCartAllocationPlanMutation,
  useUpdateCartAllocationPlanMutation,
  useDeleteCartAllocationPlanMutation,
  useLazyGetDistributionBasicPlanQuery,
  useSaveCartDistributionPlanMutation,
  useDistributeOrdersMutation,
  useUpdateCartLoadingConfigurationMutation,
  useLazyGetCartTypesQuery,
  useUpdateBulkCartLoadingConfigurationMutation,
  useUpdateCartLoadingStateMutation,
  useLazyGetCartsLoadForRestaurantByRestaurantIdQuery,
  useLazyGetCartLoadingDetailQuery,
  useCreateGroupOrderMutation,
} = distributionService;
